import { useCallback, useEffect, useRef, useState } from 'react';

const INACTIVITY_MS = 15000;

export type LeaveModalVariant = 'back' | 'inactivity' | null;

const FOCUS_IN_IFRAME_CHECK_MS = 1500;

interface UseCheckoutLeaveConfirmationOptions {
  enabled: boolean;
  onConfirmLeave: () => void;
  // When true, the inactivity modal will not be shown
  pauseInactivityWhen?: boolean;
  // When true, the inactivity timer is paused while document.activeElement is an iframe
  pauseInactivityWhenFocusedInIframe?: boolean;
}

interface UseCheckoutLeaveConfirmationResult {
  leaveModalOpen: boolean;
  leaveModalVariant: LeaveModalVariant;
  onLeave: () => void;
  onStay: () => void;
  onContinue: () => void;
}

export function useCheckoutLeaveConfirmation({
  enabled,
  onConfirmLeave,
  pauseInactivityWhen = false,
  pauseInactivityWhenFocusedInIframe = false,
}: UseCheckoutLeaveConfirmationOptions): UseCheckoutLeaveConfirmationResult {
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveModalVariant, setLeaveModalVariant] = useState<LeaveModalVariant>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabledRef = useRef(enabled);
  const isLeavingRef = useRef(false);
  const onConfirmLeaveRef = useRef(onConfirmLeave);
  const pauseInactivityWhenRef = useRef(pauseInactivityWhen);
  const pauseInactivityWhenFocusedInIframeRef = useRef(pauseInactivityWhenFocusedInIframe);
  enabledRef.current = enabled;
  onConfirmLeaveRef.current = onConfirmLeave;
  pauseInactivityWhenRef.current = pauseInactivityWhen;
  pauseInactivityWhenFocusedInIframeRef.current = pauseInactivityWhenFocusedInIframe;

  const onLeave = useCallback(() => {
    setLeaveModalOpen(false);
    setLeaveModalVariant(null);
    isLeavingRef.current = true;
    window.history.back();
    onConfirmLeaveRef.current();
  }, []);

  const onStay = useCallback(() => {
    setLeaveModalOpen(false);
    setLeaveModalVariant(null);
  }, []);

  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    inactivityTimerRef.current = setTimeout(() => {
      inactivityTimerRef.current = null;
      if (!enabledRef.current) return;
      if (pauseInactivityWhenRef.current) return;
      if (
        pauseInactivityWhenFocusedInIframeRef.current &&
        document.activeElement?.tagName === 'IFRAME'
      ) {
        return;
      }
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      setLeaveModalVariant('inactivity');
      setLeaveModalOpen(true);
    }, INACTIVITY_MS);
  }, []);

  const onContinue = useCallback(() => {
    setLeaveModalOpen(false);
    setLeaveModalVariant(null);
    startInactivityTimer();
  }, [startInactivityTimer]);

  useEffect(() => {
    if (!enabled) {
      isLeavingRef.current = false;
      return;
    }
    isLeavingRef.current = false;
    const checkoutUrl = window.location.href;
    window.history.pushState({ checkout: true }, '', checkoutUrl);

    const handlePopState = () => {
      if (isLeavingRef.current) return;
      if (!enabledRef.current) return;
      const url = window.location.href;
      window.history.replaceState({ checkout: true }, '', url);
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      setLeaveModalVariant('back');
      setLeaveModalOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !pauseInactivityWhenFocusedInIframe) return;
    const intervalId = setInterval(() => {
      if (!enabledRef.current) return;
      if (document.activeElement?.tagName === 'IFRAME') {
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
          inactivityTimerRef.current = null;
        }
      }
    }, FOCUS_IN_IFRAME_CHECK_MS);
    return () => clearInterval(intervalId);
  }, [enabled, pauseInactivityWhenFocusedInIframe]);

  useEffect(() => {
    if (!enabled) return;
    if (pauseInactivityWhen && inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    const resetTimer = () => {
      if (leaveModalOpen) return;
      if (pauseInactivityWhenRef.current) return;
      if (
        pauseInactivityWhenFocusedInIframeRef.current &&
        document.activeElement?.tagName === 'IFRAME'
      ) {
        return;
      }
      startInactivityTimer();
    };

    const events = ['mousemove', 'keydown', 'touchstart', 'click'];
    events.forEach(ev => document.addEventListener(ev, resetTimer));
    if (!pauseInactivityWhen) startInactivityTimer();

    return () => {
      events.forEach(ev => document.removeEventListener(ev, resetTimer));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [
    enabled,
    leaveModalOpen,
    pauseInactivityWhen,
    pauseInactivityWhenFocusedInIframe,
    startInactivityTimer,
  ]);

  return {
    leaveModalOpen,
    leaveModalVariant,
    onLeave,
    onStay,
    onContinue,
  };
}
